import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, FormControl, InputLabel, MenuItem,
  Select, TextField, Typography, Alert, Divider, InputAdornment,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  ContactConfig,
  ContactEnquiryRequest,
  getContactConfig,
  submitContactEnquiry,
} from '../../api/brandingApi';

const QUERY_TYPE_LABELS: Record<string, string> = {
  SEO_TOOLS:       'SEO Tools',
  PRODUCT_ENQUIRY: 'Product Enquiry',
  GENERAL:         'General',
  APPOINTMENT:     'Appointment Request',
};

const formatTime12 = (hour24: number, minute: number) => {
  const h = ((hour24 % 24) + 24) % 24;
  const hour12 = ((h + 11) % 12) + 1;
  return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const formatStartSlotLabel = (hour24: number) => {
  const start = formatTime12(hour24, 0);
  const end = formatTime12(hour24, 30);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  return `${start} - ${end} ${period}`;
};

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: formatStartSlotLabel(i),
}));

interface ContactUsWidgetProps {
  sourceSystem?: string;
}

export default function ContactUsWidget({ sourceSystem }: ContactUsWidgetProps) {
  const resolvedSourceSystem = sourceSystem?.trim() || 'asneris';
  const [config, setConfig]     = useState<ContactConfig | null>(null);
  const [configErr, setConfigErr] = useState(false);

  const [form, setForm] = useState<ContactEnquiryRequest>({
    queryType: '',
    name: '',
    email: '',
    contactNumber: '',
    preferredLanguage: 'en',
    preferredTimezone: '',
    preferredHour: undefined,
    detail: '',
  });

  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ContactEnquiryRequest, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]       = useState<string | null>(null);
  const [error, setError]           = useState<string | null>(null);

  // Load config (query types, languages, timezones) on mount
  useEffect(() => {
    getContactConfig(resolvedSourceSystem)
      .then(setConfig)
      .catch(() => setConfigErr(true));
  }, [resolvedSourceSystem]);

  const isAppointment = form.queryType === 'APPOINTMENT';

  const set = (field: keyof ContactEnquiryRequest, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => ({ ...prev, [field]: undefined }));
  };

  // ── Validation ────────────────────────────────────────────────────────

  const HTML_RE  = /<[^>]*>/;
  const PHONE_RE = /^[+\d\s().-]*$/;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function validate(): boolean {
    const errs: Partial<Record<keyof ContactEnquiryRequest, string>> = {};

    if (!form.queryType) {
      errs.queryType = 'Please select a query type.';
    }

    const name = form.name.trim();
    if (!name) {
      errs.name = 'Name is required.';
    } else if (name.length > 255) {
      errs.name = 'Name must be 255 characters or fewer.';
    } else if (HTML_RE.test(name)) {
      errs.name = 'Name must not contain HTML or script tags.';
    }

    const email = form.email.trim();
    if (!email) {
      errs.email = 'Email address is required.';
    } else if (!EMAIL_RE.test(email)) {
      errs.email = 'Please enter a valid email address.';
    } else if (email.length > 320) {
      errs.email = 'Email must be 320 characters or fewer.';
    }

    const phone = form.contactNumber?.trim() ?? '';
    if (phone && !PHONE_RE.test(phone)) {
      errs.contactNumber = 'Contact number may only contain digits, spaces, +, -, (, ).';
    }

    const detail = form.detail?.trim() ?? '';
    if (detail.length > 4000) {
      errs.detail = 'Details must be 4000 characters or fewer.';
    } else if (HTML_RE.test(detail)) {
      errs.detail = 'Details must not contain HTML or script tags.';
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload: ContactEnquiryRequest = {
        sourceSystem: resolvedSourceSystem,
        queryType:  form.queryType,
        name:       form.name.trim(),
        email:      form.email.trim(),
        contactNumber: form.contactNumber || undefined,
        detail:     form.detail || undefined,
        ...(isAppointment && {
          preferredLanguage:  form.preferredLanguage || undefined,
          preferredTimezone:  form.preferredTimezone || undefined,
          preferredHour:      form.preferredHour,
        }),
      };
      const res = await submitContactEnquiry(payload);
      setSuccess(res.message);
    } catch {
      setError('Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          maxHeight: '100%',
          minHeight: 0,
          overflowY: 'auto',
          pr: 0.5,
          '& .MuiFormControl-root': {
          flexShrink: 0,
          },
          '& .MuiInputBase-root': {
          minHeight: 56,
          },
          '& .MuiInputBase-root.MuiInputBase-multiline': {
          minHeight: 112,
          alignItems: 'flex-start',
          },
          '& .MuiFormHelperText-root': {
          flexShrink: 0,
          },
          '& .MuiButton-root': {
          flexShrink: 0,
          minHeight: 48,
          },
          }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" fontWeight={700} gutterBottom>Enquiry Received</Typography>
        <Typography color="text.secondary">{success}</Typography>
        <Button sx={{ mt: 3 }} variant="outlined" onClick={() => {
          setSuccess(null);
          setFieldErrors({});
          setForm({ queryType: '', name: '', email: '', contactNumber: '', preferredLanguage: 'en', preferredTimezone: '', preferredHour: undefined, detail: '' });
        }}>
          Submit Another
        </Button>
      </Box>
    );
  }

  if (configErr) {
    return <Alert severity="error">Could not load contact form configuration. Please refresh the page.</Alert>;
  }

  if (!config) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Query Type */}
      <FormControl fullWidth required error={!!fieldErrors.queryType}>
        <InputLabel>Query Type</InputLabel>
        <Select
          value={form.queryType}
          label="Query Type"
          onChange={e => set('queryType', e.target.value)}
        >
          {config.queryTypes.map(qt => (
            <MenuItem key={qt} value={qt}>{QUERY_TYPE_LABELS[qt] ?? qt}</MenuItem>
          ))}
        </Select>
        {fieldErrors.queryType && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{fieldErrors.queryType}</Typography>
        )}
      </FormControl>

      {/* Name */}
      <TextField
        label="Name"
        required
        fullWidth
        value={form.name}
        onChange={e => set('name', e.target.value)}
        inputProps={{ maxLength: 255 }}
        error={!!fieldErrors.name}
        helperText={fieldErrors.name}
      />

      {/* Email */}
      <TextField
        label="Email Address"
        type="email"
        required
        fullWidth
        value={form.email}
        onChange={e => set('email', e.target.value)}
        inputProps={{ maxLength: 320 }}
        error={!!fieldErrors.email}
        helperText={fieldErrors.email}
      />

      {/* Contact Number */}
      <TextField
        label="Contact Number"
        fullWidth
        value={form.contactNumber}
        onChange={e => set('contactNumber', e.target.value)}
        placeholder="+1 555 000 0000"
        InputProps={{
          startAdornment: <InputAdornment position="start">📞</InputAdornment>,
        }}
        inputProps={{ maxLength: 50 }}
        helperText={fieldErrors.contactNumber ?? 'Include country code, e.g. +44 7700 900000'}
        error={!!fieldErrors.contactNumber}
      />

      {/* Appointment fields */}
      {isAppointment && (
        <>
          <Divider sx={{ my: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Appointment Preferences</Typography>
          </Divider>

          {/* Language */}
          <FormControl fullWidth>
            <InputLabel>Preferred Language</InputLabel>
            <Select
              value={form.preferredLanguage ?? 'en'}
              label="Preferred Language"
              onChange={e => set('preferredLanguage', e.target.value)}
            >
              <MenuItem value="en">English</MenuItem>
              {/* {config.languages.map(l => (
                <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
              ))} */}
            </Select>
          </FormControl>

          {/* Timezone */}
          <FormControl fullWidth>
            <InputLabel>Preferred Timezone</InputLabel>
            <Select
              value={form.preferredTimezone ?? ''}
              label="Preferred Timezone"
              onChange={e => set('preferredTimezone', e.target.value)}
            >
              <MenuItem value=""><em>No preference</em></MenuItem>
              {config.timezones.map(tz => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Preferred Hour */}
          <FormControl fullWidth>
            <InputLabel>Preferred Time to Contact</InputLabel>
            <Select
              value={form.preferredHour ?? ''}
              label="Preferred Time to Contact"
              onChange={e => set('preferredHour', e.target.value === '' ? undefined : Number(e.target.value))}
            >
              <MenuItem value=""><em>No preference</em></MenuItem>
              {HOURS.map(h => (
                <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {/* Detail */}
      <TextField
        label="Details"
        multiline
        minRows={4}
        fullWidth
        value={form.detail}
        onChange={e => set('detail', e.target.value)}
        inputProps={{ maxLength: 4000 }}
        error={!!fieldErrors.detail}
        helperText={fieldErrors.detail ?? `${(form.detail ?? '').length}/4000`}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
      >
        {submitting ? 'Submitting…' : 'Submit Enquiry'}
      </Button>
    </Box>
  );
}
